import React, { useState } from 'react';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface ReservationData {
  date: string;
  timeSlot: string;
  forfait: string;
  prix: number;
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

const ReservationEtudePosturale: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date(2026, 0, 1));
  const [reservationData, setReservationData] = useState<ReservationData>({
    date: '',
    timeSlot: '',
    forfait: '',
    prix: 0,
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const steps = [
    { number: 1, title: "Date & Heure", icon: "📅" },
    { number: 2, title: "Forfait", icon: "🚴" },
    { number: 3, title: "Coordonnées", icon: "👤" },
    { number: 4, title: "Confirmation", icon: "✓" }
  ];

  const forfaits = [
    {
      id: 'complete',
      name: 'Étude Posturale Complète',
      description: 'Analyse dynamique complète, réglages sur-mesure, rapport détaillé',
      duration: '2h',
      prix: 250,
      features: [
        'Entretien personnalisé',
        'Analyse vidéo dynamique',
        'Réglages complets du vélo',
        'Ajustement des cales',
        'Rapport PDF détaillé',
        'Suivi post-étude'
      ]
    },
    {
      id: 'premium',
      name: 'Étude Posturale Premium',
      description: 'Analyse approfondie avec suivi personnalisé',
      duration: '3h',
      prix: 350,
      features: [
        'Tout le forfait Complet',
        'Analyse 3D avancée',
        'Test de puissance',
        'Recommandations matériel',
        'Session de suivi incluse',
        'Support prioritaire 3 mois'
      ],
      popular: true
    }
  ];

  const timeSlots: TimeSlot[] = [
    { time: '09:00', available: true },
    { time: '10:00', available: false },
    { time: '11:00', available: true },
    { time: '14:00', available: true },
    { time: '15:00', available: true },
    { time: '16:00', available: false },
    { time: '17:00', available: true }
  ];

  // Calendar helpers
  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1; // Adjust for Monday start
  };

  const formatMonth = (date: Date): string => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const formatSelectedDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isToday = (day: number): boolean => {
    const today = new Date();
    return today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear();
  };

  const isPastDay = (day: number): boolean => {
    const today = new Date();
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    today.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const handleDateSelect = (day: number) => {
    const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = selected.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setSelectedTimeSlot('');
    setReservationData({ ...reservationData, date: dateStr, timeSlot: '' });
  };

  const handleTimeSlotSelect = (time: string) => {
    setSelectedTimeSlot(time);
    setReservationData({ ...reservationData, timeSlot: time });
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleForfaitSelect = (forfait: typeof forfaits[0]) => {
    setReservationData({
      ...reservationData,
      forfait: forfait.name,
      prix: forfait.prix
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setReservationData({ ...reservationData, [name]: value });
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePayment = () => {
    alert('Redirection vers le paiement sécurisé...');
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1:
        return !!selectedDate && !!selectedTimeSlot;
      case 2:
        return !!reservationData.forfait;
      case 3:
        return !!reservationData.name && !!reservationData.email && !!reservationData.phone;
      default:
        return true;
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toISOString().split('T')[0];
      const isSelected = selectedDate === dateStr;
      const dayOfWeek = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isPast = isPastDay(day);
      const isTodayDate = isToday(day);
      const isDisabled = isWeekend || isPast;

      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleDateSelect(day)}
          disabled={isDisabled}
          className={`h-12 w-12 rounded-xl flex items-center justify-center text-sm font-medium transition-all duration-300 relative
            ${isSelected
              ? 'bg-gradient-to-br from-zinc-900 to-zinc-700 text-white shadow-lg scale-105'
              : isTodayDate && !isDisabled
                ? 'bg-zinc-100 text-zinc-900 ring-2 ring-zinc-400'
                : isDisabled
                  ? 'text-zinc-300 cursor-not-allowed'
                  : 'hover:bg-zinc-100 text-zinc-700 cursor-pointer hover:scale-105'}
          `}
        >
          {day}
          {isTodayDate && !isSelected && (
            <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-zinc-500 rounded-full"></span>
          )}
        </button>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 p-6 w-full max-w-md">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handlePrevMonth}
            className="p-3 hover:bg-zinc-100 rounded-xl transition-all duration-300 group"
          >
            <svg className="w-5 h-5 text-zinc-600 group-hover:text-zinc-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-xl font-bold capitalize text-zinc-900">{formatMonth(currentMonth)}</h3>
          <button
            onClick={handleNextMonth}
            className="p-3 hover:bg-zinc-100 rounded-xl transition-all duration-300 group"
          >
            <svg className="w-5 h-5 text-zinc-600 group-hover:text-zinc-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Week days header */}
        <div className="grid grid-cols-7 gap-1 mb-3">
          {weekDays.map(day => (
            <div key={day} className="h-10 flex items-center justify-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    );
  };

  const renderTimeSlots = () => {
    if (!selectedDate) return null;

    return (
      <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 p-6 w-full max-w-xs mt-6 lg:mt-0 lg:ml-6">
        <h4 className="text-lg font-bold text-zinc-900 mb-4">
          Créneaux disponibles
        </h4>
        <p className="text-sm text-zinc-500 mb-4">
          {formatSelectedDate(selectedDate)}
        </p>
        <div className="space-y-2">
          {timeSlots.map((slot) => (
            <button
              key={slot.time}
              onClick={() => slot.available && handleTimeSlotSelect(slot.time)}
              disabled={!slot.available}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-between
                ${selectedTimeSlot === slot.time
                  ? 'bg-gradient-to-r from-zinc-900 to-zinc-700 text-white shadow-lg'
                  : slot.available
                    ? 'bg-zinc-50 hover:bg-zinc-100 text-zinc-900 border border-zinc-200 hover:border-zinc-300'
                    : 'bg-zinc-50 text-zinc-300 cursor-not-allowed line-through'
                }
              `}
            >
              <span>{slot.time}</span>
              {slot.available && selectedTimeSlot !== slot.time && (
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
              )}
              {selectedTimeSlot === slot.time && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex flex-col lg:flex-row justify-center items-start">
            {renderCalendar()}
            {renderTimeSlots()}
          </div>
        );

      case 2:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {forfaits.map((forfait) => (
              <div
                key={forfait.id}
                onClick={() => handleForfaitSelect(forfait)}
                className={`relative bg-white rounded-2xl p-6 cursor-pointer transition-all duration-300 border-2
                  ${reservationData.forfait === forfait.name
                    ? 'border-zinc-900 shadow-2xl scale-[1.02]'
                    : 'border-zinc-200 hover:border-zinc-400 hover:shadow-xl'}
                  ${forfait.popular ? 'ring-2 ring-amber-400 ring-offset-2' : ''}
                `}
              >
                {forfait.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg">
                    POPULAIRE
                  </div>
                )}

                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-zinc-900">{forfait.name}</h3>
                    <p className="text-zinc-500 text-sm mt-1">{forfait.description}</p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                    ${reservationData.forfait === forfait.name
                      ? 'border-zinc-900 bg-zinc-900'
                      : 'border-zinc-300'}
                  `}>
                    {reservationData.forfait === forfait.name && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-4xl font-black text-zinc-900">{forfait.prix}€</span>
                  <span className="text-zinc-500">/ {forfait.duration}</span>
                </div>

                <ul className="space-y-3">
                  {forfait.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-zinc-700">
                      <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );

      case 3:
        return (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-zinc-900">Vos coordonnées</h3>
                <p className="text-zinc-500 text-sm mt-1">Pour finaliser votre réservation</p>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Nom complet <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={reservationData.name}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Email <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={reservationData.email}
                    onChange={handleInputChange}
                    placeholder="jean.dupont@email.com"
                    className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Téléphone <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex">
                    <div className="flex items-center px-4 bg-zinc-100 border border-r-0 border-zinc-200 rounded-l-xl">
                      <span className="text-lg">🇫🇷</span>
                      <span className="ml-2 text-sm text-zinc-600 font-medium">+33</span>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={reservationData.phone}
                      onChange={handleInputChange}
                      placeholder="6 12 34 56 78"
                      className="flex-1 px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-r-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Message (optionnel)
                  </label>
                  <textarea
                    name="message"
                    value={reservationData.message}
                    onChange={handleInputChange}
                    placeholder="Douleurs spécifiques, objectifs particuliers..."
                    rows={3}
                    className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
              {/* Success Header */}
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-700 p-8 text-center">
                <div className="w-20 h-20 bg-white/10 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Récapitulatif</h3>
                <p className="text-zinc-300 mt-2">Vérifiez votre réservation</p>
              </div>

              {/* Details */}
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                  <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📅</span>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Date & Heure</p>
                    <p className="font-semibold text-zinc-900">{formatSelectedDate(reservationData.date)}</p>
                    <p className="text-zinc-700">{reservationData.timeSlot}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                  <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🚴</span>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Forfait</p>
                    <p className="font-semibold text-zinc-900">{reservationData.forfait}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                  <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Contact</p>
                    <p className="font-semibold text-zinc-900">{reservationData.name}</p>
                    <p className="text-zinc-600 text-sm">{reservationData.email}</p>
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="border-t border-zinc-200 p-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-zinc-900">Total à payer</span>
                  <span className="text-3xl font-black text-zinc-900">{reservationData.prix}€</span>
                </div>
                <p className="text-sm text-zinc-500 mt-2">
                  Paiement sécurisé par carte bancaire
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
      <Header />

      {/* Main Content */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-black text-zinc-900 mb-4">
              Réserver mon étude posturale
            </h1>
            <p className="text-zinc-600 text-lg max-w-2xl mx-auto">
              Optimisez votre position et votre performance avec notre expertise bikefitting
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-center max-w-3xl mx-auto">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div
                    className={`flex flex-col items-center cursor-pointer transition-all duration-300
                      ${step.number <= currentStep ? 'opacity-100' : 'opacity-40'}
                    `}
                    onClick={() => step.number < currentStep && setCurrentStep(step.number)}
                  >
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl transition-all duration-300 shadow-lg
                      ${currentStep === step.number
                        ? 'bg-gradient-to-br from-zinc-900 to-zinc-700 text-white scale-110'
                        : currentStep > step.number
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white text-zinc-400 border border-zinc-200'}
                    `}>
                      {currentStep > step.number ? '✓' : step.icon}
                    </div>
                    <span className={`mt-2 text-sm font-medium hidden md:block
                      ${currentStep === step.number ? 'text-zinc-900' : 'text-zinc-500'}
                    `}>
                      {step.title}
                    </span>
                  </div>

                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 rounded-full transition-all duration-500
                      ${step.number < currentStep ? 'bg-emerald-500' : 'bg-zinc-200'}
                    `} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="min-h-[500px] flex items-center justify-center">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center max-w-4xl mx-auto mt-12">
            {currentStep > 1 ? (
              <button
                onClick={handleBack}
                className="px-6 py-3 rounded-xl font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-300 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour
              </button>
            ) : (
              <div></div>
            )}

            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center gap-2 shadow-lg
                  ${canProceed()
                    ? 'bg-gradient-to-r from-zinc-900 to-zinc-700 text-white hover:shadow-xl hover:scale-105'
                    : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'}
                `}
              >
                Continuer
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                onClick={handlePayment}
                className="px-8 py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-emerald-600 to-emerald-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Payer {reservationData.prix}€
              </button>
            )}
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default ReservationEtudePosturale;
